import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import BarometreLogo from '../components/BarometreLogo';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col">
      <PublicHeader />
      <main id="main-content" tabIndex={-1}>

      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white pt-16">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-60" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 mb-6">
            <BarometreLogo
              decorative
              variant="onPrimary"
              className="h-16 w-16 sm:h-20 sm:w-20 drop-shadow-lg shrink-0"
            />
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Baromètre Social
              <span className="block text-white/90 mt-1">Temps Réel</span>
            </h1>
          </div>
          <p className="text-xl text-white/90 max-w-2xl leading-relaxed mb-8">
            Micro-sondages anonymes pour mesurer le climat social en quelques secondes.
            Détectez les signaux faibles avant qu'il ne soit trop tard.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
          >
            Essayer gratuitement
          </Link>
        </div>
      </header>

      {/* Cibles — Pour qui ? */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-primary-600 mb-4">
            Pour qui ?
          </h2>
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">
            Un outil pour tous les types d&apos;organisations
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Entreprises */}
            <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm hover:border-primary-200 hover:shadow-md transition-all">
              <div className="text-3xl mb-4" aria-hidden>🏢</div>
              <h4 className="text-lg font-bold text-slate-900 mb-3">Entreprises</h4>
              <p className="text-slate-600 text-sm mb-4">
                TPE, PME ou grands groupes — pilotez le climat social de vos équipes, 
                anticipez le turnover et l&apos;absentéisme, repérez les signaux faibles 
                avant qu&apos;une crise n&apos;éclate.
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Enquêtes climat et bien-être</li>
                <li>• Charge de travail, management</li>
                <li>• Rapports pour la direction et les RH</li>
              </ul>
            </div>
            {/* Associations */}
            <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm hover:border-primary-200 hover:shadow-md transition-all">
              <div className="text-3xl mb-4" aria-hidden>🤝</div>
              <h4 className="text-lg font-bold text-slate-900 mb-3">Associations</h4>
              <p className="text-slate-600 text-sm mb-4">
                Donnez la parole à vos bénévoles, volontaires et adhérents. 
                Mesurez la motivation et le climat interne sans les lourdeurs 
                d&apos;un questionnaire classique.
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Sondages ultra rapides pour les bénévoles</li>
                <li>• Motivation et engagement</li>
                <li>• Prévention du décrochage</li>
              </ul>
            </div>
            {/* EPCI / Mairies */}
            <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm hover:border-primary-200 hover:shadow-md transition-all">
              <div className="text-3xl mb-4" aria-hidden>🏛️</div>
              <h4 className="text-lg font-bold text-slate-900 mb-3">EPCI & Mairies</h4>
              <p className="text-slate-600 text-sm mb-4">
                Écoutez vos agents territoriaux. Un baromètre adapté au dialogue social 
                territorial : satisfaction des équipes, prévention des risques psycho-sociaux, 
                vue par service ou par site.
              </p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Climat social des services publics</li>
                <li>• Comparaison inter-sites et intercommunalités</li>
                <li>• Rapports pour les élus et la direction</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problème */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-primary-600 mb-4">
            Le défi
          </h2>
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
            Les organisations pilotaient à l&apos;aveugle leur climat social
          </h3>
          <ul className="space-y-3 text-slate-600">
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">•</span>
              Perception tardive des tensions
            </li>
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">•</span>
              Enquêtes longues et peu engageantes
            </li>
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">•</span>
              Méfiance des collaborateurs sur l'anonymat
            </li>
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">•</span>
              Rapports RH statiques, difficiles à exploiter
            </li>
          </ul>
        </div>
      </section>

      {/* Agitation */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-amber-50/60">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-700 mb-4">
            Sans outil adapté
          </h2>
          <p className="text-xl text-slate-700 italic mb-6">
            &laquo; On nous demande notre avis, mais rien ne change. &raquo;
          </p>
          <p className="text-slate-600">
            Les signaux faibles passent inaperçus. L'absentéisme augmente. Le turnover s'accélère.
            Les conflits internes se multiplient. Les talents partent.
          </p>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-primary-600 mb-4">
            La solution
          </h2>
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">
            Un baromètre social en temps réel, anonyme et intelligent
          </h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex gap-4 p-5 rounded-xl bg-primary-50 border border-primary-100">
              <span className="text-2xl" aria-hidden>⚡</span>
              <div>
                <h4 className="font-semibold text-slate-900">Réponses en 30 secondes</h4>
                <p className="text-slate-600 text-sm mt-1">Micro-sondages ultra courts pour un engagement maximal</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-xl bg-primary-50 border border-primary-100">
              <span className="text-2xl" aria-hidden>🔒</span>
              <div>
                <h4 className="font-semibold text-slate-900">100% anonyme</h4>
                <p className="text-slate-600 text-sm mt-1">Tokens uniques, aucune trace, séparation des données</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-xl bg-primary-50 border border-primary-100">
              <span className="text-2xl" aria-hidden>📊</span>
              <div>
                <h4 className="font-semibold text-slate-900">Indicateurs en temps réel</h4>
                <p className="text-slate-600 text-sm mt-1">Climat, évolution, participation à portée de clic</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-xl bg-primary-50 border border-primary-100">
              <span className="text-2xl" aria-hidden>🚨</span>
              <div>
                <h4 className="font-semibold text-slate-900">Alertes automatiques</h4>
                <p className="text-slate-600 text-sm mt-1">Dégradations détectées, décisions préventives</p>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/pricing"
              className="inline-block px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700"
            >
              Voir les tarifs
            </Link>
          </div>
        </div>
      </section>

      {/* Comment ça marche (Collaborateurs) */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-primary-600 mb-4">
            Participer
          </h2>
          <h3 className="text-2xl font-bold text-slate-900 mb-6">
            Vous avez reçu un lien de sondage ?
          </h3>
          <p className="text-slate-600 mb-6">
            Ouvrez le lien unique envoyé par votre organisation. Répondez en moins de 30 secondes
            avec des scores ou des boutons rapides. Vos réponses restent totalement anonymes
            et non traçables.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <span className="text-lg">😡</span>
              <span className="text-lg">😕</span>
              <span className="text-lg">😐</span>
              <span className="text-lg">🙂</span>
              <span className="text-lg">😄</span>
              <span className="text-slate-500 ml-1">— Score émotionnel</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <span>👍 Oui</span>
              <span>👎 Non</span>
              <span>⚖️ Mitigé</span>
              <span className="text-slate-500 ml-1">— Questions secondaires</span>
            </div>
          </div>
          <p className="mt-6 text-slate-500 text-sm">
            🔒 Vos réponses sont 100% anonymes et non traçables.
          </p>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold text-slate-900 mb-8">
            Une décision basée sur des données concrètes
          </h3>
          <div className="flex flex-wrap justify-center gap-8 text-slate-600">
            <span>Micro-sondages ultra courts</span>
            <span>Temps réel</span>
            <span>Alertes automatiques</span>
            <span>Vision prédictive</span>
          </div>
        </div>
      </section>
      </main>

      <PublicFooter />
    </div>
  );
}
